import { v4 as uuidv4 } from 'uuid';
import { ErrorName, PlayerError } from '../../errors';
import BaseEngine from '../BaseEngine';
const MP3_MIME_TYPE = 'audio/mpeg';
const METADATA_LENGTH_SPECIFIER_MULTIPLIER = 16;
const BACK_BUFFER_SEC = 60;
const formatToMimeTypeMap = {
    mp3: MP3_MIME_TYPE,
};
export default class IcyEngine extends BaseEngine {
    static name = 'icy';
    static get MediaSourceApi() {
        return window.ManagedMediaSource ?? window.MediaSource ?? null;
    }
    static browserSupportsFormat(format) {
        const MS = IcyEngine.MediaSourceApi;
        return Boolean(MS?.isTypeSupported?.(formatToMimeTypeMap[format ?? '']));
    }
    #fetchAbortController = null;
    #textDecoder = new TextDecoder();
    #sourceBuffer;
    #isStopped = false;
    #loadId = null;
    async load() {
        const MediaSourceApi = IcyEngine.MediaSourceApi;
        if (!MediaSourceApi)
            throw new Error('MediaSource unsupported');
        this.#isStopped = false;
        this.#loadId = uuidv4();
        this.#fetchAbortController = null;
        this._mediaSource = new MediaSourceApi();
        this._mediaSource.addEventListener('sourceopen', this.#onSourceOpen);
        return super.load();
    }
    destroy() {
        this.#isStopped = true;
        this.#loadId = null;
        this._mediaSource?.removeEventListener('sourceopen', this.#onSourceOpen);
        this.#sourceBuffer?.removeEventListener('error', this.#onSourceError);
        this.#abortFetch();
        this.#endMediaSourceStream();
        super.destroy();
    }
    #waitForUpdateEnd = () => new Promise((resolve) => this.#sourceBuffer?.addEventListener('updateend', () => resolve(), {
        once: true,
    }));
    async #appendAndTrim(chunk, currentTime) {
        this.#sourceBuffer?.appendBuffer(chunk);
        await this.#waitForUpdateEnd();
        if (this.#sourceBuffer?.buffered.length &&
            currentTime - this.#sourceBuffer.buffered.start(0) > BACK_BUFFER_SEC &&
            !this.#sourceBuffer.updating) {
            const cutOff = currentTime - BACK_BUFFER_SEC / 2;
            this.#sourceBuffer.remove(0, cutOff);
            await this.#waitForUpdateEnd();
        }
    }
    #abortFetch() {
        try {
            this.#fetchAbortController?.abort();
        }
        catch {
        }
    }
    async #endMediaSourceStream(reason) {
        if (this.#sourceBuffer?.updating) {
            await this.#waitForUpdateEnd();
        }
        if (this._mediaSource?.readyState === 'open') {
            this._mediaSource.endOfStream(reason);
        }
        this.#sourceBuffer = undefined;
    }
    async #fetchStream() {
        this.#fetchAbortController = new AbortController();
        const tryFetch = async (withIcy) => {
            const response = await fetch(this._options.src, {
                headers: withIcy ? { 'Icy-Metadata': '1' } : undefined,
                signal: this.#fetchAbortController?.signal,
            });
            if (!response.ok || !response.body) {
                throw new Error(`IcyEngine stream fetch failed. Icy headers: ${withIcy.toString()}, Status: ${response.status}`);
            }
            return {
                reader: response.body.getReader(),
                icyMetaInt: Number(response.headers.get('icy-metaint')) || 0,
            };
        };
        const onError = (e) => {
            if (e?.name !== 'AbortError') {
                this.emit('error', PlayerError.createError(ErrorName.MetadataError, e?.message));
            }
            throw e;
        };
        try {
            return tryFetch(true);
        }
        catch (e) {
            onError(e);
        }
        try {
            return tryFetch(false);
        }
        catch (e) {
            onError(e);
        }
    }
    async #streamLoop() {
        const { reader, icyMetaInt } = (await this.#fetchStream()) || {};
        if (!reader) {
            return;
        }
        if (!icyMetaInt) {
            while (true) {
                const { done, value } = await reader.read();
                if (done || this.#isStopped)
                    break;
                await this.#appendAndTrim(value, this.getCurrentTime());
            }
            return;
        }
        const acc = [];
        while (!this.#isStopped) {
            const { done, value } = await reader.read();
            if (this._mediaSource.readyState === 'ended' || this.#isStopped) {
                break;
            }
            if (done) {
                this.#endMediaSourceStream();
                break;
            }
            acc.push(...value);
            while (acc.length >= icyMetaInt + 1) {
                const audioBytes = acc.splice(0, icyMetaInt);
                await this.#appendAndTrim(Uint8Array.from(audioBytes), this.getCurrentTime());
                const lenByte = acc.shift();
                if (lenByte === undefined)
                    break;
                const metaLen = lenByte * METADATA_LENGTH_SPECIFIER_MULTIPLIER;
                if (acc.length < metaLen) {
                    acc.unshift(lenByte);
                    break;
                }
                if (!metaLen) {
                    continue;
                }
                try {
                    const metaBytes = acc.splice(0, metaLen);
                    const rawMetadata = this.#textDecoder.decode(Uint8Array.from(metaBytes));
                    if (!rawMetadata) {
                        throw new Error('Received empty metadata');
                    }
                    this.emit('metadata', { icy: rawMetadata });
                }
                catch (e) {
                    const message = `Error parsing ICY metadata: ${e?.message}`;
                    this.emit('error', PlayerError.createError(ErrorName.MetadataError, message));
                }
            }
        }
    }
    #onSourceOpen = async () => {
        const loadId = this.#loadId;
        this.#sourceBuffer = this._mediaSource.addSourceBuffer(MP3_MIME_TYPE);
        this.#sourceBuffer.mode = 'sequence';
        this.#sourceBuffer.addEventListener('error', this.#onSourceError);
        try {
            await this.#streamLoop();
        }
        catch (err) {
            const wasAborted = err?.name === 'AbortError';
            if (wasAborted || this.#isStopped || loadId !== this.#loadId) {
                return;
            }
            this.emit('error', PlayerError.createError(ErrorName.PlaybackErrorNetwork, err.message));
        }
    };
    #onSourceError = () => this.destroy();
}
